import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Phone, TrendingUp, AlertTriangle, Clock, Activity, RefreshCw,
  ArrowUpRight, ArrowDownRight, Loader2, Zap, CheckCircle2,
  MessageCircle, AlertOctagon, User, Share2, Star, Trophy, ExternalLink,
  Tags as TagsIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DateRangePicker } from '../components/ui/date-range-picker';
import {
  BarChart, Bar, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
  LabelList
} from 'recharts';

// Distinct color per score-distribution bucket, keyed by range label so each bar
// reads differently and colors stay correct even when a bucket has zero calls
// (empty buckets are dropped by the query, which would shift index-based colors).
const SCORE_COLORS = {
  '0-30':   '#FB7185', // red — very low
  '30-50':  '#FB923C', // orange
  '50-60':  '#FBBF24', // amber
  '60-75':  '#A3E635', // lime
  '75-90':  '#22D3C5', // teal
  '90-100': '#34D399', // green — excellent
};
// Fixed colors per analysis source so Claude vs Gemma read consistently
// across the dashboard (mirrors AgentAnalyticsPage).
const SOURCE_COLORS = { claude: '#22D3C5', gemma: '#2BB6FF' };

// Canonical taxonomy for the Purposes / Outcomes sections. call_purpose and
// call_outcome are free-text from the model, so raw values are bucketed into
// this fixed set and always shown in this order.
const PURPOSE_BUCKETS = [
  { key: 'appointment_booking', label: 'Appointment Booking' },
  { key: 'health_enquiry', label: 'Health Enquiry' },
  { key: 'follow_up', label: 'Follow Up' },
  { key: 'other', label: 'Other' },
  { key: 'lead', label: 'Lead' },
];
const OUTCOME_BUCKETS = [
  { key: 'resolved', label: 'Resolved' },
  { key: 'callback_scheduled', label: 'Callback Scheduled' },
  { key: 'unresolved', label: 'Unresolved' },
  { key: 'transferred', label: 'Transferred' },
];

const normalizeTaxonomyKey = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

function classifyPurpose(raw) {
  const n = normalizeTaxonomyKey(raw);
  if (n.includes('appointment') || n.includes('booking')) return 'appointment_booking';
  if (n.includes('health') || n.includes('enquir') || n.includes('inquir')) return 'health_enquiry';
  if (n.includes('follow')) return 'follow_up';
  if (n.includes('lead')) return 'lead';
  return 'other'; // complaint and anything else fold into Other
}

function classifyOutcome(raw) {
  const n = normalizeTaxonomyKey(raw);
  if (n.includes('unresolved')) return 'unresolved'; // check before 'resolved'
  if (n.includes('resolved')) return 'resolved';
  if (n.includes('callback') || n.includes('call_back')) return 'callback_scheduled';
  if (n.includes('transfer')) return 'transferred';
  return null; // values outside the taxonomy are not shown
}

// Bucket raw [{ _id, count }] rows into the fixed taxonomy, preserving order.
function bucketTaxonomy(items, buckets, classify) {
  const counts = Object.fromEntries(buckets.map((b) => [b.key, 0]));
  (items || []).forEach((row) => {
    const key = classify(row?._id);
    if (key && key in counts) counts[key] += Number(row?.count) || 0;
  });
  return buckets.map((b) => ({ name: b.label, value: counts[b.key] }));
}
const QUALITY_COLORS = {
  excellent: '#34D399',
  good: '#22D3C5',
  fair: '#FBBF24',
  poor: '#FB7185',
};

// Date helpers (local time, YYYY-MM-DD as produced/consumed by <input type="date">)
const toDateInput = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};
const todayInput = () => toDateInput(new Date());
const daysAgoInput = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

// Shared date-range filter used by both the admin and agent dashboards.
function DateRangeFilter({ fromDate, toDate, setFromDate, setToDate, onRefresh }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <DateRangePicker
        value={{ from_date: fromDate, to_date: toDate }}
        showValue={!!(fromDate || toDate)}
        placeholder="Select date range"
        onChange={(range) => {
          setFromDate(range.from_date);
          setToDate(range.to_date);
        }}
        onClear={() => {
          // Dashboards always need a window — reset to the default last 7 days.
          setFromDate(daysAgoInput(7));
          setToDate(todayInput());
        }}
        buttonClassName="h-9 min-w-[240px]"
      />
      <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="dashboard-refresh-btn">
        <RefreshCw className="w-4 h-4" />
      </Button>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, trend, color = 'primary' }) {
  const colors = {
    primary: 'text-teal-400 bg-teal-500/10',
    warning: 'text-amber-400 bg-amber-500/10',
    danger: 'text-rose-400 bg-rose-500/10',
    info: 'text-sky-400 bg-sky-500/10',
    success: 'text-emerald-400 bg-emerald-500/10',
  };
  return (
    <div className="bg-card border border-border rounded-xl p-4 kpi-card" data-testid={`dashboard-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}


function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
      <p className="text-xs font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </p>
      ))}
    </div>
  );
}

function CompactDashboardList({ title, items, emptyLabel, accent = 'teal' }) {
  const accentStyles = {
    teal: {
      chip: 'bg-teal-500/15 text-teal-300',
      bar: 'from-teal-500 via-cyan-500 to-sky-500',
    },
    amber: {
      chip: 'bg-amber-500/15 text-amber-300',
      bar: 'from-amber-500 via-orange-500 to-rose-500',
    },
    sky: {
      chip: 'bg-sky-500/15 text-sky-300',
      bar: 'from-sky-500 via-cyan-500 to-teal-500',
    },
  };

  const styles = accentStyles[accent] || accentStyles.teal;
  const normalizedItems = Array.isArray(items) ? items : [];
  const maxValue = normalizedItems.reduce((max, item) => Math.max(max, Number(item?.value) || 0), 0);

  return (
    <div className="rounded-xl border border-border bg-background/30 p-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium ${styles.chip}`}>
          {normalizedItems.length} items
        </span>
      </div>

      <div className="space-y-2.5">
        {normalizedItems.length === 0 ? (
          <p className="text-xs text-muted-foreground">{emptyLabel}</p>
        ) : normalizedItems.map((item) => {
          const value = Number(item?.value) || 0;
          const width = maxValue > 0 ? Math.max(8, Math.round((value / maxValue) * 100)) : 0;

          return (
            <div key={item.name} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="truncate capitalize text-foreground">{item.name}</span>
                <span className="tabular-nums text-muted-foreground">{value}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-background/80">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${styles.bar}`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TagsDashboardCard({ items, summary, rangeLabel, onOpenTags, title = 'Tags', subtitle, testId = 'dashboard-tags-card' }) {
  const normalizedItems = Array.isArray(items) ? items : [];
  const activeTags = Number(summary?.active_tags) || 0;
  const tagRecords = Number(summary?.tag_records) || 0;
  const matchedTagRecords = Number(summary?.matched_tag_records) || 0;
  const topItems = normalizedItems.slice(0, 3);
  const isClickable = typeof onOpenTags === 'function';
  const Tag = isClickable ? 'button' : 'div';

  return (
    <Tag
      {...(isClickable ? { type: 'button', onClick: onOpenTags } : {})}
      className={`group rounded-xl border border-border bg-gradient-to-br from-sky-500/10 via-background/40 to-teal-500/10 p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring${isClickable ? ' hover:border-sky-400/40 hover:bg-sky-500/10 cursor-pointer' : ' cursor-default'}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/15 text-sky-300">
            <TagsIcon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
            <p className="text-[11px] text-muted-foreground">
              {subtitle || `Latest matched tags · ${rangeLabel}`}
            </p>
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-sky-300" />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-border bg-background/40 px-2.5 py-2">
          <p className="text-lg font-semibold tabular-nums text-foreground">{activeTags}</p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Active</p>
        </div>
        <div className="rounded-lg border border-border bg-background/40 px-2.5 py-2">
          <p className="text-lg font-semibold tabular-nums text-foreground">{tagRecords}</p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">All Tags </p>
        </div>
        <div className="rounded-lg border border-border bg-background/40 px-2.5 py-2">
          <p className="text-lg font-semibold tabular-nums text-foreground">{matchedTagRecords}</p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Matched</p>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {topItems.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-background/30 px-3 py-3 text-xs text-muted-foreground">
            No tag results for {rangeLabel}.
          </p>
        ) : topItems.map((item) => {
          const value = Number(item?.value) || 0;

          return (
            <div key={item.name} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/35 px-3 py-2 text-xs">
              <span className="truncate capitalize text-foreground">{item.name}</span>
              <span className="rounded-md bg-sky-500/15 px-2 py-0.5 font-medium tabular-nums text-sky-300">{value}</span>
            </div>
          );
        })}
      </div>
    </Tag>
  );
}

// Quality score trend (area chart) for a single analysis source.
function ScoreTrendCard({ title, data, color, gradientId }) {
  return (
    <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
      </div>
      {data.length > 0 ? (
        <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={260}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A6B2C7' }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#A6B2C7' }} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="score" stroke={color} fill={`url(#${gradientId})`} name="Avg Score" />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
          No trend data for this window.
        </div>
      )}
    </div>
  );
}

// Score distribution (bar chart) for a single analysis source.
function ScoreDistributionCard({ title, data }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {data.length > 0 ? (
        <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={260}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
            <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#A6B2C7' }} />
            <YAxis tick={{ fontSize: 11, fill: '#A6B2C7' }} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="count" name="Calls" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((entry, i) => (
                <Cell key={i} fill={SCORE_COLORS[entry.range] || '#22D3C5'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No data</div>
      )}
    </div>
  );
}

// Top agents leaderboard for a single analysis source.
function TopAgentsCard({ title, agents }) {
  const list = Array.isArray(agents) ? agents : [];
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      <div className="space-y-2">
        {list.slice(0, 8).map((agent, i) => (
          <div key={agent.agent_number || agent._id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/[0.03] transition-colors">
            <span className="text-xs font-mono text-muted-foreground w-5">{i + 1}</span>
            <span className="text-sm flex-1 truncate">{agent.agent_name || agent._id}</span>
            <span className="text-xs text-muted-foreground">{agent.total_calls} calls</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
              agent.avg_score >= 75 ? 'score-good' : agent.avg_score >= 60 ? 'score-review' : 'score-critical'
            }`}>
              {agent.avg_score?.toFixed(0)}
            </span>
          </div>
        ))}
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">No agent data yet</p>
        )}
      </div>
    </div>
  );
}

// Score bands used by the best-calls card. Hex (not the score-* classes) because
// the ring gauge strokes an SVG, which cannot take a Tailwind text color.
const BEST_CALL_BANDS = [
  { min: 90, color: '#34D399', label: 'Excellent' },
  { min: 75, color: '#22D3C5', label: 'Good' },
  { min: 60, color: '#FBBF24', label: 'Review' },
  { min: 0,  color: '#FB7185', label: 'Critical' },
];
const bestCallBand = (score) =>
  BEST_CALL_BANDS.find((b) => score >= b.min) || BEST_CALL_BANDS[BEST_CALL_BANDS.length - 1];

// Gold / silver / bronze for the top three, plain slate after that.
const RANK_STYLES = [
  'bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/30',
  'bg-slate-300/15 text-slate-200 ring-1 ring-slate-300/25',
  'bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/30',
];

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// Per-parameter vertical bar chart — every quality parameter on the same 0-10
// axis, so the weakest and strongest read off one scale. Bars use the admin
// dashboard's Outcomes accent (amber -> orange -> rose); no green.
function QualityParametersCard({ params, thresholds }) {
  const paramBarColor = (pct) => {
    if (pct >= thresholds.HIGH) return '#F59E0B';   // amber-500
    if (pct >= thresholds.MEDIUM) return '#F97316'; // orange-500
    return '#F43F5E';                               // rose-500
  };

  const chartData = (params || []).map((score) => {
    const val = parseFloat(score.value) || 0;
    return {
      name: score.name,
      value: val,
      max: score.max,
      fill: paramBarColor((val / score.max) * 100),
    };
  });

  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="agent-score-breakdown">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Quality Parameters
        </h3>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-amber-500" /> Good
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-orange-500" /> Needs Work
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-rose-500" /> Critical
          </span>
        </div>
      </div>
      <ResponsiveContainer
        className="focus:outline-none focus-visible:outline-none focus:ring-0"
        width="100%"
        height={280}
      >
        <BarChart data={chartData} margin={{ top: 18, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#A6B2C7' }}
            angle={-35}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis
            domain={[0, 10]}
            ticks={[0, 2, 4, 6, 8, 10]}
            tick={{ fontSize: 11, fill: '#A6B2C7' }}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
          <Bar dataKey="value" name="Score" barSize={30} radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              formatter={(v) => Number(v).toFixed(1)}
              style={{ fontSize: 11, fill: '#E6EDF7', fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// The agent's own highest-scoring calls in the window. This used to be a tab on
// the coaching screen, which is the wrong home for it: coaching is about the
// parameters you are weak in, and a list ranked by overall score answers a
// different question. Still five rows and still click-through to the call,
// presented as a table: score, when, direction, duration, purpose and outcome —
// call details only, no per-parameter scores (those belong to coaching).
//
// Scope needs no agent filter: /calls restricts an agent login to their own
// calls server-side, from the token.
function MyBestCallsCard({ fromDate, toDate }) {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.getCalls({
      from_date: fromDate,
      to_date: toDate,
      sort_by: 'effective_score',
      sort_order: 'desc',
      limit: 5,
    })
      .then((res) => {
        if (cancelled) return;
        const scored = (res?.calls || [])
          // Effective analysis: Claude's when the call was analyzed by Claude,
          // otherwise Gemma's — the same coalesce the API sorted on. Every
          // detail below the score reads from this one source so the row never
          // mixes a Claude score with Gemma parameters.
          .map((call) => {
            const src = call?.analysis?.overall_score != null ? call.analysis : call?.gemini_analysis;
            return {
              ...call,
              src: src || null,
              score: src?.overall_score ?? null,
            };
          })
          // The API sorts NULLS LAST, so unscored calls only surface when there
          // are fewer than five scored ones. They are not "best" anything.
          .filter((call) => call.score !== null && call.score !== undefined);
        setCalls(scored);
      })
      .catch(() => { if (!cancelled) setCalls([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fromDate, toDate]);

  // Average of the shown calls — a one-number answer to "how good is my best work".
  const topAverage = calls.length
    ? Math.round(calls.reduce((sum, c) => sum + Number(c.score || 0), 0) / calls.length)
    : null;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="agent-dashboard-best-calls">
      {/* Header — subtle gold wash so the section reads as the celebratory one */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-gradient-to-r from-amber-500/[0.07] via-teal-500/[0.04] to-transparent">
        <div className="w-9 h-9 rounded-lg bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/25 flex items-center justify-center shrink-0">
          <Trophy className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold uppercase tracking-wide">My Best Calls</h3>
          <p className="text-[11px] text-muted-foreground">Your highest-scoring calls in this window</p>
        </div>
        {topAverage !== null && (
          <div className="ml-auto text-right shrink-0">
            <div className="text-lg font-bold tabular-nums leading-none" style={{ color: bestCallBand(topAverage).color }}>
              {topAverage}
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">Top {calls.length} avg</div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-28 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        </div>
      ) : calls.length === 0 ? (
        <div className="py-10 flex flex-col items-center gap-2">
          <Trophy className="w-6 h-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No scored calls in this period</p>
          <p className="text-[11px] text-muted-foreground/70">Analyzed calls will show up here, best first</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="best-calls-table">
            <thead>
              <tr className="border-b border-border bg-white/[0.02]">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-12">Rank</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Score</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Date &amp; Time</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Direction</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Duration</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Purpose</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Outcome</th>
                <th className="px-4 py-2.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {calls.map((call, index) => {
                const band = bestCallBand(Number(call.score) || 0);
                const src = call.src || {};
                const purpose = String(src.call_purpose || '').replace(/_/g, ' ');
                const outcome = String(src.call_outcome || '').replace(/_/g, ' ');
                const isInbound = String(call.direction || '').toLowerCase() === 'inbound';

                return (
                  <tr
                    key={call.call_id}
                    onClick={() => navigate(`/calls/${encodeURIComponent(call.call_id)}`)}
                    data-testid="best-call-row"
                    className={`group border-b border-border/40 last:border-b-0 cursor-pointer transition-colors hover:bg-white/[0.03] ${
                      index === 0 ? 'bg-gradient-to-r from-amber-400/[0.05] to-transparent' : ''
                    }`}
                  >
                    {/* Rank medal — gold, silver, bronze */}
                    <td className="px-4 py-3">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold
                          ${RANK_STYLES[index] || 'bg-white/[0.05] text-muted-foreground ring-1 ring-white/10'}`}
                      >
                        {index + 1}
                      </span>
                    </td>

                    {/* Score — the number itself, colored by band */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold tabular-nums leading-none" style={{ color: band.color }}>
                          {call.score}
                        </span>
                        <span
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap"
                          style={{ color: band.color, backgroundColor: `${band.color}1F` }}
                        >
                          {band.label}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium">{call.date || '-'}</div>
                      {call.time && <div className="text-[11px] text-muted-foreground tabular-nums">{call.time}</div>}
                    </td>

                    <td className="px-4 py-3">
                      {call.direction ? (
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded capitalize whitespace-nowrap ${
                          isInbound ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'
                        }`}>
                          {isInbound ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                          {call.direction}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        <Clock className="w-3 h-3" />
                        {call.call_duration != null ? formatDuration(call.call_duration) : '-'}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      {purpose ? (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-white/[0.06] text-foreground/80 capitalize whitespace-nowrap">
                          {purpose}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {outcome ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300/90 capitalize whitespace-nowrap">
                          <CheckCircle2 className="w-3 h-3" />
                          {outcome}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <ExternalLink className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground transition-colors inline-block" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Agent Dashboard Component
function AgentDashboard({ agentName, fromDate, toDate, setFromDate, setToDate, data, loading, fetchData }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Format trend data once with proper type handling
  const formatNumber = (val) => parseFloat(val ?? 0);
  const trendData = (data?.daily_trend || []).map(d => ({
    date: d._id,
    // Effective per-call metric: Claude when the call was analyzed by Claude,
    // otherwise Gemma (auto-processing). Backs the unified Score Trend so the
    // line reflects the same per-call source, not just the Claude-analyzed calls.
    score: formatNumber(d.eff_avg_score ?? d.avg_score),
    total: d.total ?? 0
  }));

  // Define reusable score thresholds and utility functions
  const SCORE_THRESHOLDS = { HIGH: 75, MEDIUM: 50, LOW: 0 };
  const OUTCOME_THRESHOLDS = { HIGH: 50, MEDIUM: 25, LOW: 0 };
  const formatScore = (val, decimals = 1) => parseFloat(val ?? 0).toFixed(decimals);
  
  // Build the 8-dimension breakdown from the effective (coalesced) averages:
  // each metric uses the Claude value when the call was analyzed by Claude,
  // otherwise the Gemma (auto-processing) value. Falls back to the Claude-only
  // average if the effective key is absent.
  const buildScoreBreakdown = () => {
    const a = data?.averages || {};
    const eff = (key) => formatScore(a[`eff_${key}`] ?? a[key]);
    return [
      { name: 'Overall Score', value: eff('avg_score'), max: 100 },
      { name: 'Grammar', value: eff('avg_grammar'), max: 10 },
      { name: 'Professionalism', value: eff('avg_professionalism'), max: 10 },
      { name: 'Empathy', value: eff('avg_empathy'), max: 10 },
      { name: 'Communication', value: eff('avg_communication'), max: 10 },
      { name: 'Greeting Quality', value: eff('avg_greeting'), max: 10 },
      { name: 'Closing Quality', value: eff('avg_closing'), max: 10 },
      { name: 'Solution Score', value: eff('avg_solution'), max: 10 },
    ];
  };

  // Unified (combined) view: Claude data per call when present, otherwise Gemma.
  const activeColor = SOURCE_COLORS.claude;
  const scoreBreakdown = buildScoreBreakdown();
  const activeAvgScore = data?.averages?.eff_avg_score ?? data?.averages?.avg_score;

  // Utility function for formatting data names
  const formatDataName = (name) => name ?? 'Unknown';
  
  const purposesData = (data?.purposes ?? []).map(d => ({ 
    name: formatDataName(d._id), 
    value: d.count ?? 0 
  }));
  
  // Bucket free-text outcomes into the canonical taxonomy so only
  // Resolved / Callback Scheduled / Unresolved / Transferred are shown
  // (mirrors the admin dashboard).
  const outcomesData = bucketTaxonomy(data?.outcomes, OUTCOME_BUCKETS, classifyOutcome);
  
  // Direction data with validation and formatting
  const directionsData = (data?.directions ?? []).reduce((acc, d) => {
    try {
      if (!d || d._id == null || d.count == null) return acc;
      const directionName = String(d._id).toLowerCase().trim();
      if (!directionName || (directionName !== 'inbound' && directionName !== 'outbound')) return acc;
      const displayName = directionName === 'inbound' ? 'Inbound' : 'Outbound';
      acc.push({ 
        name: displayName, 
        value: Math.max(0, Number(d.count) || 0), 
        key: directionName,
        raw: d // debug
      });
    } catch (e) {
      console.warn('Skip invalid direction entry:', d, e);
    }
    return acc;
  }, []);

  return (
    <div className="space-y-6">
      {/* Agent Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold">
              {(agentName || 'Agent').charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{agentName || 'Agent'}</h1>
              <p className="text-sm text-muted-foreground">Agent Performance Dashboard</p>
            </div>
          </div>
        </div>
        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          onRefresh={fetchData}
        />
      </div>

      {/* KPI Cards - Agent View (combined: Claude per call when present, else Gemma) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="agent-dashboard-kpis">
        <KPICard
          icon={Phone}
          label="Total Calls"
          value={data?.total_calls || 0}
          sub={`${data?.processed_calls ?? data?.completed_calls ?? 0} processed`}
          color="primary"
        />
        <KPICard
          icon={Star}
          label="Overall Score"
          value={`${formatScore(activeAvgScore)}/100`}
          color={(() => {
            const score = parseFloat(activeAvgScore ?? 0);
            return score >= SCORE_THRESHOLDS.HIGH ? 'success' : score >= 60 ? 'warning' : 'danger';
          })()}
        />
        <KPICard
          icon={MessageCircle}
          label="Escalations"
          value={data?.escalations || 0}
          color={data?.escalations > 5 ? 'danger' : 'warning'}
        />
        <KPICard
          icon={AlertOctagon}
          label="Blame Shifts"
          value={data?.blame_shifts || 0}
          color="danger"
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily Score Trend — same area-chart design as the admin dashboard */}
        <ScoreTrendCard
          title="Quality Score Trend"
          data={trendData}
          color={activeColor}
          gradientId="agentScoreGrad"
        />

        {/* Score Breakdown Radar */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Quality Dimensions</h3>
          <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={280}>
            <RadarChart data={scoreBreakdown.map(s => ({
              name: s.name,
              value: (parseFloat(s.value) / s.max) * 100
            }))}>
              <PolarGrid stroke="hsl(220,34%,20%)" />
              <PolarAngleAxis dataKey="name" tick={{ fontSize: 9, fill: '#A6B2C7' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#A6B2C7' }} />
              <Radar name="Performance %" dataKey="value" stroke={activeColor} fill={activeColor} fillOpacity={0.6} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Call direction, then the quality parameters, in one row. The overall
          score is not repeated here — it already leads the KPI row above; this
          section is the per-parameter detail only. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Inbound/Outbound Calls */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Call Direction</h3>
            <p className="text-[10px] text-muted-foreground">Inbound = received | Outbound = placed</p>
          </div>
          {directionsData && directionsData.length > 0 ? (
            <div className="space-y-4">
              {(() => {
                try {
                  const validData = directionsData.filter(d => d && Number.isFinite(d.value) && d.value > 0);
                  if (validData.length === 0) {
                    return <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">No call direction data in selected period</div>;
                  }

                  const total = validData.reduce((sum, d) => sum + (d.value || 0), 0);
                  const DIRECTION_COLORS = { inbound: '#0EA5E9', outbound: '#14B8A6' };

                  const dominantDirection = validData[0].key;

                  return (
                    <>
                      <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={validData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {validData.map((entry) => {
                              const fillColor = DIRECTION_COLORS[entry.key] || '#64748B';
                              return <Cell key={`cell-${entry.key}`} fill={fillColor} />;
                            })}
                          </Pie>
                          <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>

                      <div className="space-y-2 pt-3 border-t border-border/50 mt-3">
                        {validData.map((direction) => {
                          const percentage = total > 0 ? ((direction.value / total) * 100).toFixed(1) : 0;
                          const isInbound = direction.key === 'inbound';
                          const isDominant = direction.key === dominantDirection;
                          return (
                            <div key={`direction-${direction.key}`} className={`flex items-center justify-between text-xs p-2 rounded ${isDominant ? 'bg-muted/30' : ''}`}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${isInbound ? 'bg-sky-500' : 'bg-teal-500'}`} />
                                <span className="font-medium">{direction.name}{isDominant && <span className="text-[10px] text-muted-foreground ml-1 font-normal">(Primary)</span>}</span>
                              </div>
                              <span className="text-muted-foreground">{direction.value} ({percentage}%)</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error rendering call direction data:', error);
                  return <div className="h-40 flex items-center justify-center text-xs text-rose-400">Error loading direction data</div>;
                }
              })()}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
              No calls found (total: {data?.total_calls || 0})&nbsp;
              <span className="text-xs">→ Try a wider date range</span>
            </div>
          )}
        </div>

        {/* Quality parameters — the leading Overall Score entry is dropped;
            the parameters are what this chart is about. */}
        <div className="lg:col-span-2">
          <QualityParametersCard params={scoreBreakdown.slice(1)} thresholds={SCORE_THRESHOLDS} />
        </div>
      </div>

      {/* Best calls — moved here from the coaching screen's removed tab. */}
      <MyBestCallsCard fromDate={fromDate} toDate={toDate} />

      {/* Bottom Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Call Purposes */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Call Purposes</h3>
          {purposesData && purposesData.length > 0 ? (
            <ResponsiveContainer className="focus:outline-none focus-visible:outline-none focus:ring-0" width="100%" height={250}>
              <BarChart data={purposesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#A6B2C7' }} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11, fill: '#A6B2C7' }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" fill="#22D3C5" radius={[4, 4, 0, 0]} name="Calls" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No purpose data available</div>
          )}
        </div>

        {/* Call Outcomes */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Call Outcomes</h3>
          {outcomesData && outcomesData.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                const total = outcomesData.reduce((sum, o) => sum + (o.value ?? 0), 0);
                if (total === 0) {
                  return <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">No outcome data for selected period</div>;
                }
                
                const getOutcomeColor = (pct) => {
                  const numPct = parseFloat(pct ?? 0);
                  if (numPct >= OUTCOME_THRESHOLDS.HIGH) return 'bg-emerald-500';
                  if (numPct >= OUTCOME_THRESHOLDS.MEDIUM) return 'bg-amber-500';
                  return 'bg-rose-500';
                };
                
                return outcomesData.map((outcome, idx) => {
                  const percentage = total > 0 ? ((outcome.value / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={`${outcome.name}-${idx}`} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="capitalize font-medium">{outcome.name?.replace(/_/g, ' ') || 'Unknown'}</span>
                        <span className="text-muted-foreground">{outcome.value ?? 0} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-700/50 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getOutcomeColor(percentage)}`}
                          style={{ width: `${Math.min(parseFloat(percentage), 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">No outcome data available</div>
          )}
        </div>

      </div>

      {/* Incident Alerts (combined analysis) */}
      {(data?.escalations > 0 || data?.blame_shifts > 0) && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Quality Alerts</h3>
          <div className="space-y-2">
            {data?.escalations > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-rose-300">Escalations Detected</p>
                  <p className="text-xs text-muted-foreground">{data?.escalations} escalation(s) in the selected period</p>
                </div>
              </div>
            )}
            {data?.blame_shifts > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertOctagon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-300">Blame Shifting Detected</p>
                  <p className="text-xs text-muted-foreground">{data?.blame_shifts} instance(s) detected in this period</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin Dashboard Component

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => daysAgoInput(7));
  const [toDate, setToDate] = useState(() => todayInput());
  const { isAdmin, isAgent, isSupervisor, user } = useAuth();

  // Fetch admin dashboard data
  const fetchAdminData = async () => {
    try {
      const dashRes = await api.getDashboard(fromDate, toDate);
      console.log('DEBUG [FRONTEND DASHBOARD]: API response received:', dashRes);
      console.log('DEBUG [FRONTEND DASHBOARD]: data.directions:', dashRes?.directions);
      console.log('DEBUG [FRONTEND DASHBOARD]: total_calls:', dashRes?.total_calls);
      setData(dashRes);
    } catch (err) {
      console.error('DEBUG [FRONTEND DASHBOARD]: Fetch error:', err);
      toast.error('Failed to load dashboard');
    }
  };

  // Fetch agent-specific analytics
  const fetchAgentData = async () => {
    try {
      if (user?.agent_number) {
        const analyticsRes = await api.getAgentAnalytics(user.agent_number, fromDate, toDate);
        console.log('DEBUG [FRONTEND AGENT]: Analytics response:', analyticsRes);
        console.log('DEBUG [FRONTEND AGENT]: agentData.directions:', analyticsRes?.directions);
        // Validate response structure
        if (!analyticsRes || typeof analyticsRes !== 'object') {
          throw new Error('Invalid analytics response structure');
        }
        setAgentData(analyticsRes);
      } else {
        console.warn('No agent name available for analytics fetch');
      }
    } catch (err) {
      console.error('Agent analytics fetch failed:', err);
      toast.error('Failed to load agent analytics: ' + (err?.message || 'Unknown error'));
    }
  };

  // Main fetch function
  const fetchData = async () => {
    setLoading(true);
    try {
      if (isAgent) {
        await fetchAgentData();
      } else {
        await fetchAdminData();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, isAgent]);

  // For agents, show AgentDashboard
  if (isAgent) {
    return <AgentDashboard
      agentName={user?.agent_name}
      fromDate={fromDate}
      toDate={toDate}
      setFromDate={setFromDate}
      setToDate={setToDate}
      data={agentData}
      loading={loading}
      fetchData={fetchData}
    />;
  }

  // For admins/supervisors, show Admin Dashboard
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const trendData = (data?.daily_trend || []).map(d => ({
    date: d._id,
    score: d.avg_score,
    calls: d.total,
    critical: d.critical
  }));

  const scoreDistData = (data?.score_distribution || []).map(d => ({
    range: d._id,
    count: d.count
  }));

  const formatDashboardLabel = (value) => {
    const raw = String(value || '').trim();
    return raw ? raw.replace(/_/g, ' ') : 'Unknown';
  };

  const outcomesData = bucketTaxonomy(data?.outcomes, OUTCOME_BUCKETS, classifyOutcome);
  const purposesData = bucketTaxonomy(data?.purposes, PURPOSE_BUCKETS, classifyPurpose);
  const tagsData = (data?.tags || []).map(d => ({ name: formatDashboardLabel(d.name), value: d.count }));
  const tagsSummary = data?.tags_summary || {};
  // AI-detected lead tags from Gemma (auto-processing) calls, shown alongside the
  // keyword (Claude) tags so the section covers both analysis sources.
  const aiTagsData = (data?.ai_tags || []).map(d => ({ name: formatDashboardLabel(d.name), value: d.count }));
  const aiTagsSummary = data?.ai_tags_summary || {};
  const rangeLabel = `${fromDate} → ${toDate}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Healthcare call quality overview</p>
        </div>
        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          onRefresh={fetchData}
        />
      </div>

      {/* Combined analysis: Claude data per call when present, otherwise Gemma. */}
      <div className="space-y-6" data-testid="dashboard-panel">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KPICard icon={Phone} label="Total Calls" value={data?.total_calls || 0} color="primary" />
          <KPICard icon={TrendingUp} label="Avg Score" value={`${Number(data?.avg_score || 0).toFixed(2)}/100`} color="info" />
          <KPICard icon={AlertTriangle} label="Critical Flags" value={data?.critical_calls || 0} color="danger" />
          <KPICard icon={Clock} label="Avg Handle Time" value={`${Math.round(data?.avg_handle_time || 0)}s`} color="warning" />
          <KPICard icon={Activity} label="Processing" value={data?.processing || 0} sub={`${data?.completed || 0} completed`} color="info" />
          <KPICard icon={CheckCircle2} label="Escalations" value={data?.escalations || 0} color="danger" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ScoreTrendCard title="Quality Score Trend" data={trendData} color={SOURCE_COLORS.claude} gradientId="scoreGrad" />
          <ScoreDistributionCard title="Score Distribution" data={scoreDistData} />
        </div>

        {/* Top Agents + Purposes / Outcomes / Tags */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopAgentsCard title="Top Agents" agents={data?.top_agents} />
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Call Purposes, Outcomes &amp; Tags</h3>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Window</p>
                <p className="text-xs text-foreground">{rangeLabel}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <CompactDashboardList title="Purposes" items={purposesData} emptyLabel="No purpose data" accent="teal" />
              <CompactDashboardList title="Outcomes" items={outcomesData} emptyLabel="No outcome data" accent="amber" />
              <TagsDashboardCard
                items={tagsData}
                summary={tagsSummary}
                rangeLabel={rangeLabel}
                title="Keyword Tags"
                subtitle={`Keyword-matched tags · ${rangeLabel}`}
                onOpenTags={isSupervisor ? undefined : () => navigate('/tags')}
              />
              <TagsDashboardCard
                items={aiTagsData}
                summary={aiTagsSummary}
                rangeLabel={rangeLabel}
                title="AI Lead Tags"
                subtitle={`AI-detected leads · ${rangeLabel}`}
                testId="dashboard-ai-tags-card"
                onOpenTags={isSupervisor ? undefined : () => navigate('/tags')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Polling Status (shared across sources) */}
      {isAdmin && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${data?.polling_active ? 'bg-amber-400 status-pulse' : 'bg-emerald-400'}`} />
            <span className="text-xs text-muted-foreground">
              {data?.polling_active ? 'Polling active...' : `Last poll: ${data?.last_poll ? new Date(data.last_poll).toLocaleString() : 'Never'}`}
            </span>
          </div>
          {data?.poll_error && <p className="text-xs text-rose-400 mt-1">{data.poll_error}</p>}
        </div>
      )}
    </div>
  );
}

